import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import type { AmlPayer } from '@shared/api/contracts/aml';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  MonthPicker,
  ReasonFactorDialog,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  SkeletonStatus,
  StatusBanner,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TypographyMuted,
  useToast,
} from '@shared/ui';
import { chargeSourceKey, chargeStatusKey, currentUtcMonth, isClosedMonth } from '@entities/aml-billing';
import { getApiErrorMessage } from '@shared/lib/get-api-error-message';
import { isApiError } from '@shared/api/errors/errors';
import { swrKeys } from '@shared/api/swr/keys';
import { useApiServices } from '@shared/lib/use-api-services';
import { formatDateTime } from '@shared/lib/format-date';

const decimalText = (value: unknown): string => (typeof value === 'string' ? value : value == null ? '' : String(value));

export function AmlBillingPanel() {
  const { t } = useTranslation('cabinet');
  const { toast } = useToast();
  const services = useApiServices();
  const [month, setMonth] = useState(currentUtcMonth());
  const [merchantId, setMerchantId] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [editing, setEditing] = useState<'settings' | 'merchant' | null>(null);
  const [confirm, setConfirm] = useState<'settings' | 'merchant' | 'invoice' | 'waive' | null>(null);
  const [loading, setLoading] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [payer, setPayer] = useState<AmlPayer>('PROCESSING');
  const [price, setPrice] = useState('');
  const [currency, setCurrency] = useState('USDT');
  const [overridePayer, setOverridePayer] = useState<'inherit' | AmlPayer>('inherit');
  const [overridePrice, setOverridePrice] = useState('');
  const [overrideFree, setOverrideFree] = useState('');
  const restoreEdit = useRef<'settings' | 'merchant' | null>(null);

  const settings = useSWR(swrKeys.aml.billingSettings(), () => services.aml.getBillingSettings());
  const summary = useSWR(month ? swrKeys.aml.chargesSummary(month) : null, () => services.aml.getChargesSummary(month));
  const charges = useSWR(month ? swrKeys.aml.adminCharges(month, merchantId) : null, () =>
    services.aml.getAdminCharges(month, merchantId || undefined),
  );
  const merchant = useSWR(merchantId ? swrKeys.aml.merchantBilling(merchantId) : null, () =>
    services.aml.getMerchantBilling(merchantId),
  );

  const fail = (error: unknown) => {
    toast(getApiErrorMessage(error, t, 'compliance.errors.generic'), 'error');
    if (isApiError(error) && error.status === 409) {
      void settings.mutate();
      void charges.mutate();
      void summary.mutate();
    }
  };

  const run = async (action: () => Promise<void>, message: string) => {
    setLoading(true);
    try {
      await action();
      toast(message, 'success');
      restoreEdit.current = null;
      setEditing(null);
      setConfirm(null);
      setSelected([]);
      void settings.mutate();
      void summary.mutate();
      void charges.mutate();
      void merchant.mutate();
    } catch (error) {
      fail(error);
    } finally {
      setLoading(false);
    }
  };

  const prepareSettings = () => {
    const current = settings.data;
    if (!current) return;
    setEnabled(current.enabled);
    setPayer(current.defaultPayer);
    setPrice(decimalText(current.pricePerCheck));
    setCurrency(current.currency);
    setEditing('settings');
  };

  const prepareMerchant = () => {
    const current = merchant.data?.override;
    setOverridePayer(current?.payer ?? 'inherit');
    setOverridePrice(decimalText(current?.pricePerCheck));
    setOverrideFree(current?.freeChecksPerMonth == null ? '' : String(current.freeChecksPerMonth));
    setEditing('merchant');
  };

  const askConfirm = (kind: 'settings' | 'merchant') => {
    restoreEdit.current = kind;
    setEditing(null);
    setConfirm(kind);
  };

  const download = async () => {
    try {
      const file = await services.aml.downloadAdminCharges(month, merchantId || undefined);
      const url = URL.createObjectURL(file.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `aml-charges-${month}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      fail(error);
    }
  };

  const rows = charges.data?.data ?? [];
  const closed = isClosedMonth(month);

  return (
    <div className="page-stack">
      <section className="surface-card space-y-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h2 className="text-base font-medium">{t('amlBilling.tariff')}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{t('amlBilling.tariffHint')}</p>
          </div>
          <Button type="button" className="w-full shrink-0 sm:w-auto" disabled={!settings.data} onClick={prepareSettings}>
            {t('amlBilling.editTariff')}
          </Button>
        </div>
        {settings.isLoading && !settings.data ? (
          <SkeletonStatus className="grid gap-3 sm:grid-cols-3" label={t('workspace.loading')}>
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </SkeletonStatus>
        ) : null}
        {settings.error && !settings.data ? <StatusBanner variant="destructive">{t('workspace.loadError')}</StatusBanner> : null}
        {settings.data ? (
          <dl className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-xl border border-border bg-background px-4 py-3">
              <dt className="text-xs text-muted-foreground">{t('amlBilling.enabled')}</dt>
              <dd className="mt-1">
                <span
                  className={
                    settings.data.enabled
                      ? 'inline-flex rounded-full bg-success px-2.5 py-0.5 text-xs font-medium text-success-foreground'
                      : 'inline-flex rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground'
                  }
                >
                  {settings.data.enabled ? t('amlBilling.on') : t('amlBilling.off')}
                </span>
              </dd>
            </div>
            <div className="rounded-xl border border-border bg-background px-4 py-3">
              <dt className="text-xs text-muted-foreground">{t('amlBilling.defaultPayer')}</dt>
              <dd className="mt-1 text-sm font-medium">{t(`amlBilling.payer.${settings.data.defaultPayer}`)}</dd>
            </div>
            <div className="rounded-xl border border-border bg-background px-4 py-3">
              <dt className="text-xs text-muted-foreground">{t('amlBilling.price')}</dt>
              <dd className="mt-1 text-sm font-medium tabular-nums">
                {decimalText(settings.data.pricePerCheck)} {settings.data.currency}
              </dd>
            </div>
          </dl>
        ) : null}
      </section>

      <section className="surface-card space-y-5">
        <div>
          <h2 className="text-base font-medium">{t('amlBilling.summaryTitle')}</h2>
        </div>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[minmax(0,16rem)_minmax(0,16rem)_1fr]">
          <div className="space-y-1.5">
            <Label htmlFor="aml-month">{t('amlBilling.month')}</Label>
            <MonthPicker id="aml-month" value={month} onChange={setMonth} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="aml-merchant">{t('amlBilling.merchantId')}</Label>
            <Input
              id="aml-merchant"
              inputMode="numeric"
              value={merchantId}
              onChange={(event) => setMerchantId(event.target.value.replace(/\D/g, ''))}
            />
          </div>
          <div className="flex flex-col gap-2 sm:flex-row md:col-span-2 xl:col-span-1 xl:items-end xl:justify-end">
            <Button type="button" variant="outline" className="w-full sm:w-auto" disabled={!merchantId} onClick={prepareMerchant}>
              {t('amlBilling.merchantOverride')}
            </Button>
            <Button type="button" variant="secondary" className="w-full sm:w-auto" onClick={() => void download()}>
              {t('amlBilling.csv')}
            </Button>
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          {t('amlBilling.processingTotal', {
            count: summary.data?.processing.count ?? 0,
            amount: summary.data?.processing.amount ?? '0',
          })}
        </p>
        <div className="min-w-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('compliance.columns.merchant')}</TableHead>
                <TableHead>{t('amlBilling.checks')}</TableHead>
                <TableHead>{t('amlBilling.billable')}</TableHead>
                <TableHead>{t('amlBilling.processingPaid')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {summary.isLoading && !summary.data ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <SkeletonStatus label={t('workspace.loading')}>
                      <Skeleton className="h-10 w-full" />
                    </SkeletonStatus>
                  </TableCell>
                </TableRow>
              ) : null}
              {(summary.data?.merchants ?? []).map((item) => (
                <TableRow key={String(item.userId ?? item.email ?? 'none')}>
                  <TableCell className="max-w-[14rem] truncate">{item.email || '—'}</TableCell>
                  <TableCell className="tabular-nums">{item.checks}</TableCell>
                  <TableCell className="tabular-nums">{item.billable}</TableCell>
                  <TableCell className="tabular-nums">{item.processingPaid}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </section>

      <section className="surface-card space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-base font-medium">{t('amlBilling.chargesTitle')}</h2>
          <div className="flex flex-col gap-2 sm:flex-row">
            {closed && merchantId ? (
              <Button type="button" variant="secondary" className="w-full sm:w-auto" onClick={() => setConfirm('invoice')}>
                {t('amlBilling.markInvoiced')}
              </Button>
            ) : null}
            <Button type="button" variant="destructive" className="w-full sm:w-auto" disabled={!selected.length} onClick={() => setConfirm('waive')}>
              {t('amlBilling.waive')}
            </Button>
          </div>
        </div>
        {charges.isLoading && !charges.data ? (
          <SkeletonStatus label={t('workspace.loading')}>
            <Skeleton className="h-24 w-full" />
          </SkeletonStatus>
        ) : null}
        {!charges.isLoading && !rows.length ? (
          <TypographyMuted className="block py-8 text-center">{t('amlBilling.empty')}</TypographyMuted>
        ) : null}
        {rows.length ? (
          <div className="min-w-0 overflow-x-auto">
            <Table className="min-w-[48rem]">
              <TableHeader>
                <TableRow>
                  <TableHead />
                  <TableHead>{t('withdrawals.columns.date')}</TableHead>
                  <TableHead>{t('amlBilling.sourceColumn')}</TableHead>
                  <TableHead>{t('withdrawals.columns.amount')}</TableHead>
                  <TableHead>{t('withdrawals.columns.status')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <Checkbox
                        checked={selected.includes(row.id)}
                        aria-label={row.id}
                        onCheckedChange={(checked) =>
                          setSelected((currentIds) =>
                            checked === true ? [...currentIds, row.id] : currentIds.filter((id) => id !== row.id),
                          )
                        }
                      />
                    </TableCell>
                    <TableCell className="whitespace-nowrap">{formatDateTime(row.createdAt)}</TableCell>
                    <TableCell>{t(chargeSourceKey(row.sourceType))}</TableCell>
                    <TableCell className="tabular-nums">{row.price} {row.currency}</TableCell>
                    <TableCell>{t(chargeStatusKey(row.status, row.statusReason))}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : null}
      </section>

      <Dialog open={editing === 'settings'} onOpenChange={(open) => { if (!open) setEditing(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('amlBilling.editTariff')}</DialogTitle>
            <DialogDescription>{t('amlBilling.tariffDialogHint')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-4 rounded-xl border border-border bg-background px-4 py-3">
              <div className="min-w-0">
                <p className="text-sm font-medium">{t('amlBilling.enabled')}</p>
                <p className="text-xs text-muted-foreground">{enabled ? t('amlBilling.on') : t('amlBilling.off')}</p>
              </div>
              <Switch
                checked={enabled}
                aria-label={t('amlBilling.enabled')}
                onCheckedChange={(checked) => setEnabled(checked === true)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="aml-payer">{t('amlBilling.defaultPayer')}</Label>
              <Select value={payer} onValueChange={(value) => setPayer(value as AmlPayer)}>
                <SelectTrigger id="aml-payer"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="MERCHANT">{t('amlBilling.payer.MERCHANT')}</SelectItem>
                  <SelectItem value="PROCESSING">{t('amlBilling.payer.PROCESSING')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="aml-price">{t('amlBilling.price')}</Label>
                <Input id="aml-price" inputMode="decimal" value={price} onChange={(event) => setPrice(event.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="aml-currency">{t('amlBilling.currency')}</Label>
                <Input
                  id="aml-currency"
                  value={currency}
                  maxLength={8}
                  onChange={(event) => setCurrency(event.target.value.toUpperCase())}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEditing(null)}>
              {t('amlBilling.cancel')}
            </Button>
            <Button type="button" onClick={() => askConfirm('settings')}>
              {t('amlPolicy.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editing === 'merchant'} onOpenChange={(open) => { if (!open) setEditing(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('amlBilling.merchantOverride')}</DialogTitle>
            <DialogDescription>{t('amlBilling.overrideDialogHint')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="override-payer">{t('amlBilling.defaultPayer')}</Label>
              <Select value={overridePayer} onValueChange={(value) => setOverridePayer(value as 'inherit' | AmlPayer)}>
                <SelectTrigger id="override-payer"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="inherit">{t('amlBilling.inherit')}</SelectItem>
                  <SelectItem value="MERCHANT">{t('amlBilling.payer.MERCHANT')}</SelectItem>
                  <SelectItem value="PROCESSING">{t('amlBilling.payer.PROCESSING')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="override-price">{t('amlBilling.price')}</Label>
                <Input
                  id="override-price"
                  inputMode="decimal"
                  value={overridePrice}
                  placeholder={t('amlBilling.inherit')}
                  onChange={(event) => setOverridePrice(event.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="override-free">{t('amlBilling.freeQuota')}</Label>
                <Input
                  id="override-free"
                  inputMode="numeric"
                  value={overrideFree}
                  placeholder={t('amlBilling.inherit')}
                  onChange={(event) => setOverrideFree(event.target.value)}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEditing(null)}>
              {t('amlBilling.cancel')}
            </Button>
            <Button type="button" onClick={() => askConfirm('merchant')}>
              {t('amlPolicy.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ReasonFactorDialog
        open={confirm !== null}
        title={t(`amlBilling.dialog.${confirm ?? 'settings'}`)}
        description={t('amlBilling.dialogDescription')}
        confirmLabel={t('amlPolicy.save')}
        confirmVariant={confirm === 'waive' ? 'destructive' : 'default'}
        loading={loading}
        onOpenChange={(open) => {
          if (!open) {
            const restore = restoreEdit.current;
            restoreEdit.current = null;
            setConfirm(null);
            if (restore) setEditing(restore);
          }
        }}
        onConfirm={(value) => {
          if (confirm === 'settings') {
            void run(async () => {
              if (!settings.data) return;
              await services.aml.patchBillingSettings({
                version: settings.data.version,
                enabled,
                defaultPayer: payer,
                pricePerCheck: price,
                currency,
                secondFactor: value.secondFactor,
              });
            }, t('amlPolicy.applied'));
          }
          if (confirm === 'merchant') {
            void run(async () => {
              await services.aml.setMerchantBilling(merchantId, {
                payer: overridePayer === 'inherit' ? null : overridePayer,
                pricePerCheck: overridePrice.trim() ? overridePrice.trim() : null,
                freeChecksPerMonth: overrideFree.trim() ? Number(overrideFree) : null,
                secondFactor: value.secondFactor,
              });
            }, t('amlPolicy.applied'));
          }
          if (confirm === 'invoice') {
            void run(async () => {
              await services.aml.markChargesInvoiced({
                merchantId: Number(merchantId),
                month,
                reason: value.reason,
                secondFactor: value.secondFactor,
              });
            }, t('amlBilling.invoiced'));
          }
          if (confirm === 'waive') {
            void run(async () => {
              await services.aml.waiveCharges({ ids: selected.slice(0, 500), reason: value.reason, secondFactor: value.secondFactor });
            }, t('amlBilling.waived'));
          }
        }}
      />
    </div>
  );
}
